// ===== Events Scheduling Page (Backend Integrated) =====
import React from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { PlusIcon, CalendarIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { dtLocale } from '../../../utils/dateLocale';

const EventsSchedulingPage = () => {
  const { canAccess, events, loading, refreshData } = useVenueDashboard();

  if (!canAccess(['MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Events" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Manager role or higher required.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString(dtLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleTimeString(dtLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Categorize events
  const upcomingEvents = events?.filter(e => new Date(e.date || e.startDate) > new Date()) || [];
  const pastEvents = events?.filter(e => new Date(e.date || e.startDate) <= new Date()) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Events Scheduling" />
      
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Your Events
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={refreshData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                color: '#6b7280',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
              Refresh
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#8b5cf6',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              data-testid="create-event-button"
            >
              <PlusIcon style={{ width: '18px', height: '18px' }} />
              Create Event
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Total Events</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '4px 0 0 0' }}>
              {events?.length || 0}
            </p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Upcoming</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6', margin: '4px 0 0 0' }}>
              {upcomingEvents.length}
            </p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Past Events</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#9ca3af', margin: '4px 0 0 0' }}>
              {pastEvents.length}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#8b5cf6',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Empty State */}
        {!loading && (!events || events.length === 0) && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <CalendarIcon style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
              No Events Yet
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
              Create your first event to engage customers
            </p>
            <button
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#8b5cf6',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create Event
            </button>
          </div>
        )}

        {/* Upcoming Events */}
        {!loading && upcomingEvents.length > 0 && (
          <>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Upcoming Events
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {upcomingEvents.map((event, index) => (
                <EventCard 
                  key={event._id || event.id || index} 
                  event={event} 
                  formatDate={formatDate}
                  formatTime={formatTime}
                  isUpcoming={true}
                />
              ))}
            </div>
          </>
        )}

        {/* Past Events */}
        {!loading && pastEvents.length > 0 && (
          <>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#9ca3af', marginBottom: '12px' }}>
              Past Events
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pastEvents.slice(0, 5).map((event, index) => (
                <EventCard 
                  key={event._id || event.id || index} 
                  event={event} 
                  formatDate={formatDate}
                  formatTime={formatTime}
                  isUpcoming={false}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EventCard = ({ event, formatDate, formatTime, isUpcoming }) => (
  <div
    style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderLeft: `4px solid ${isUpcoming ? '#8b5cf6' : '#9ca3af'}`,
      opacity: isUpcoming ? 1 : 0.7
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
          {event.title || event.name || 'Untitled Event'}
        </h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '14px' }}>
            <CalendarIcon style={{ width: '16px', height: '16px' }} />
            {formatDate(event.date || event.startDate)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '14px' }}>
            <ClockIcon style={{ width: '16px', height: '16px' }} />
            {formatTime(event.date || event.startDate)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '14px' }}>
            👥 {event.attendees?.length || event.registrations || 0} interested
          </div>
        </div>
        {event.description && (
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '12px 0 0 0', lineHeight: 1.4 }}>
            {event.description.slice(0, 100)}{event.description.length > 100 ? '...' : ''}
          </p>
        )}
      </div>
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: isUpcoming ? '#ede9fe' : '#f3f4f6',
        color: isUpcoming ? '#6d28d9' : '#6b7280'
      }}>
        {isUpcoming ? 'Upcoming' : 'Past'}
      </span>
    </div>
  </div>
);

export default EventsSchedulingPage;
